import { Injectable } from '@angular/core';
import { Shortcut } from 'app/layout/common/shortcuts/shortcuts.types';
import { map, Observable, ReplaySubject, switchMap, take, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ShortcutsService {
    private _shortcuts: ReplaySubject<Shortcut[]> = new ReplaySubject<
        Shortcut[]
    >(1);

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for shortcuts
     */
    get shortcuts$(): Observable<Shortcut[]> {
        return this._shortcuts.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all messages
     */
    getAll(): Observable<Shortcut[]> {
        const seed: Shortcut[] = [];
        this._shortcuts.next(seed);
        return of(seed);
    }

    /**
     * Create a shortcut
     *
     * @param shortcut
     */
    create(shortcut: Shortcut): Observable<Shortcut> {
        return this.shortcuts$.pipe(
            take(1),
            switchMap((shortcuts) =>
                of({ ...shortcut }).pipe(
                    map((newShortcut) => {
                        this._shortcuts.next([...shortcuts, newShortcut]);
                        return newShortcut;
                    })
                )
            )
        );
    }

    /**
     * Update the shortcut
     *
     * @param id
     * @param shortcut
     */
    update(id: string, shortcut: Shortcut): Observable<Shortcut> {
        return this.shortcuts$.pipe(
            take(1),
            switchMap((shortcuts) =>
                of({ ...shortcut, id }).pipe(
                    map((updatedShortcut: Shortcut) => {
                        const index = shortcuts.findIndex((item) => item.id === id);
                        if (index > -1) {
                            shortcuts[index] = updatedShortcut;
                        }
                        this._shortcuts.next([...shortcuts]);
                        return updatedShortcut;
                    })
                )
            )
        );
    }

    /**
     * Delete the shortcut
     *
     * @param id
     */
    delete(id: string): Observable<boolean> {
        return this.shortcuts$.pipe(
            take(1),
            switchMap((shortcuts) =>
                of(true).pipe(
                    map((isDeleted: boolean) => {
                        const index = shortcuts.findIndex((item) => item.id === id);
                        if (index > -1) {
                            shortcuts.splice(index, 1);
                        }
                        this._shortcuts.next([...shortcuts]);
                        return isDeleted;
                    })
                )
            )
        );
    }
}
